import { useState } from 'react';

import type { BookingGarment, BookingWearer } from '@/api/bookings';
import { DesignPhoto } from '@/components/DesignPhoto';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate, formatDateTime } from '@core/format/dates';
import { formatAmount, formatMoney } from '@core/format/money';
import { humanizeEnum } from '@core/format/text';
import { alterationNarrative } from '@/pages/print/alterationNarrative';
import { fittingState } from './fitting';
import { movesFor } from '@core/bookings/garmentMoves';
import { garmentTone, readinessTone } from '@core/bookings/tones';
import { withReference } from '@core/units/referenceNumber';

/**
 * The party: every wearer on a booking, and what each of them is wearing.
 *
 * <p>
 * <strong>Grouped by person, because a person collects an outfit.</strong> One wearer takes a
 * sherwani, a safa and a mojari, and their readiness is a fact about all three together — two of
 * three ready is nobody able to leave the shop dressed. §12 asks for exactly this view: "one
 * wedding party, many people, ... showing every member's fitting and readiness status".
 * </p>
 *
 * <p>
 * <strong>Built from markup rather than through `DataView`, and that is the deliberate exception
 * to the rule.</strong> `DataView` exists so a flat list renders as a table on a desk and as cards
 * on a phone from one column definition; this is neither — it is a two-level hierarchy inside a
 * detail page, and flattening it into columns would put the wearer in a repeated cell, which is
 * the grouping the whole screen exists to show. It also avoids the `text-nowrap` that `style.css`
 * puts on every `td`, so this reflows on a phone instead of becoming a horizontal strip. A *list*
 * screen reaching for `Table` directly is still the thing to catch in review.
 * </p>
 */
export function PartyTable({
  wearers,
  payerName = '',
  currency = '',
  onMoveGarment,
  onViewPhotos,
  onShowHistory,
  onPrintDocket,
  onFitting,
}: {
  wearers: BookingWearer[];
  /**
   * Who is paying, so the wearer who is also the payer is not marked "not on file".
   *
   * <p>
   * <strong>Matched on the name because there is nothing better to match on.</strong> A wearer's
   * `customerId` is null even when the wearer *is* the customer, deliberately: the counter creates
   * that first wearer from the payer, and a clerk who then renames it — the father pays, the groom
   * wears — would leave an id pointing at the wrong person. Null survives a rename; a link does not.
   * </p>
   *
   * <p>
   * So the marker is suppressed by name instead, which is exactly as strong as the claim it makes:
   * a row reading the payer's name is not a person the shop has no record of. Empty by default, so
   * the print path and the tests behave as they did.
   * </p>
   */
  payerName?: string;
  /** The tenant's symbol. Empty by default — the money quad owns the currency, not this screen. */
  currency?: string;
  /**
   * Raised with the garment whose state is being moved. Optional, so the table stays a pure
   * rendering of a booking for the print path and its tests — a component that always needed a
   * mutation to render is one that cannot be tested without one.
   */
  onMoveGarment?: ((garment: BookingGarment) => void) | undefined;
  /**
   * Raised with the garment whose pictures are being opened. Optional for the same reason
   * `onMoveGarment` is: the print path renders this table with neither.
   */
  onViewPhotos?: ((garment: BookingGarment) => void) | undefined;
  /** Opens the garment timeline. Absent on a surface that has nowhere to put a panel. */
  onShowHistory?: ((garment: BookingGarment) => void) | undefined;
  /**
   * Raised with the garment whose own docket is being printed. Optional like the other two — a
   * docket is a sheet that travels with a garment, so one garment is the unit a workshop works in,
   * and the row the clerk is already looking at is where that belongs.
   */
  onPrintDocket?: ((garment: BookingGarment) => void) | undefined;
  /**
   * Raised with the wearer whose fitting is being booked, moved or marked done. Optional like the
   * other three, and for the same reason: the print path renders this table with none of them.
   */
  onFitting?: ((wearer: BookingWearer) => void) | undefined;
}) {
  return (
    <div className="divide-border-color divide-y">
      {wearers.map((wearer) => (
        <WearerBlock
          key={wearer.id}
          wearer={wearer}
          isPayer={
            payerName.trim() !== '' &&
            wearer.name.trim().toLocaleLowerCase() === payerName.trim().toLocaleLowerCase()
          }
          currency={currency}
          onMoveGarment={onMoveGarment}
          onViewPhotos={onViewPhotos}
          onShowHistory={onShowHistory}
          onPrintDocket={onPrintDocket}
          onFitting={onFitting}
        />
      ))}
    </div>
  );
}

function WearerBlock({
  wearer,
  isPayer,
  currency,
  onMoveGarment,
  onViewPhotos,
  onShowHistory,
  onPrintDocket,
  onFitting,
}: {
  wearer: BookingWearer;
  /** Whether this row is the person paying, whose record the booking obviously holds. */
  isPayer: boolean;
  currency: string;
  onMoveGarment?: ((garment: BookingGarment) => void) | undefined;
  onViewPhotos?: ((garment: BookingGarment) => void) | undefined;
  /** Opens the garment timeline. Absent on a surface that has nowhere to put a panel. */
  onShowHistory?: ((garment: BookingGarment) => void) | undefined;
  onPrintDocket?: ((garment: BookingGarment) => void) | undefined;
  onFitting?: ((wearer: BookingWearer) => void) | undefined;
}) {
  const fitting = fittingState(wearer);

  const packages = [
    ...new Set(
      wearer.garments
        .map((garment) => garment.packageName)
        .filter((name): name is string => Boolean(name)),
    ),
  ];

  return (
    <section>
      <header className="bg-light flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5">
        <span className="bg-primary-transparent text-primary flex size-6 flex-none items-center justify-center rounded-full text-[10px] font-bold">
          {initials(wearer.name)}
        </span>

        <span className="min-w-0">
          <span className="text-[13px] font-semibold text-gray-900">{wearer.name}</span>
          <span className="text-default text-[11.5px]">
            {wearer.relation && ` · ${wearer.relation}`}
            {/*
              A groomsman is measured and fitted but does not pay; forcing a customer record for
              each one pollutes the master that duplicate detection runs on, so a null id is the
              ordinary case and this marker says the shop has no record of that person.

              Never on the payer. The counter now creates the first wearer from the customer, so
              this row carries the payer's name on the commonest booking of all — and "Hiren Patel ·
              not on file" beneath a header naming Hiren Patel as the customer is a flat
              contradiction. The id is still null there, and deliberately: see `payerName`.
            */}
            {wearer.customerId === null && !isPayer && ' · not on file'}
          </span>
        </span>

        {packages.map((name) => (
          <span
            key={name}
            className="bg-primary-transparent text-primary rounded px-1.5 py-0.5 text-[10.5px] font-medium"
          >
            {name}
          </span>
        ))}

        <span className="ms-auto flex items-center gap-2.5">
          <StatusBadge tone={readinessTone(wearer.readyCount, wearer.garmentCount)}>
            {wearer.readyCount} of {wearer.garmentCount} ready
          </StatusBadge>
          <span className="font-mono-nums text-[13px] font-semibold text-gray-900">
            {currency}
            {formatAmount(wearer.rentalAmount)}
          </span>
        </span>
      </header>

      {/*
        The fitting strip, which used to render on `appointmentOn && !completedOn` alone — so a
        wearer nobody had booked in and a wearer already fitted looked identical, because both
        showed nothing. Those are work outstanding and work finished, and a screen that draws them
        the same way cannot be used to get a wedding party measured. See `fitting.ts`.

        It is only actionable where a caller offers the action: the print path renders this table
        with no handlers at all, and a button on a printed sheet is nonsense.
      */}
      <div className="border-border-color flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-4 py-1.5 text-[11.5px]">
        {fitting === 'done' ? (
          <span className="text-default">
            {/* The appointment is the wearer's, not any one garment's — it covers all of them. */}
            Fitted {formatDateTime(wearer.fittingCompletedOn)}
            {wearer.fittingByEmployeeName ? ` — ${wearer.fittingByEmployeeName}` : ''}
          </span>
        ) : fitting === 'scheduled' ? (
          <span className="text-default">
            Fitting appointment {formatDateTime(wearer.fittingAppointmentOn)}
          </span>
        ) : (
          <span className="text-warning">No fitting booked</span>
        )}

        {onFitting && (
          <button
            type="button"
            onClick={() => onFitting(wearer)}
            className="text-primary hover:text-dark ms-auto cursor-pointer text-[11.5px] font-medium"
          >
            {fitting === 'scheduled'
              ? 'Mark fitted'
              : fitting === 'done'
                ? 'Correct it'
                : 'Book a fitting'}
          </button>
        )}
      </div>

      <ul className="divide-border-color divide-y">
        {wearer.garments.map((garment) => (
          <GarmentRow
            key={garment.id}
            garment={garment}
            currency={currency}
            onMoveGarment={onMoveGarment}
            onViewPhotos={onViewPhotos}
            onShowHistory={onShowHistory}
            onPrintDocket={onPrintDocket}
          />
        ))}
      </ul>
    </section>
  );
}

function GarmentRow({
  garment,
  currency,
  onMoveGarment,
  onViewPhotos,
  onShowHistory,
  onPrintDocket,
}: {
  garment: BookingGarment;
  currency: string;
  onMoveGarment?: ((garment: BookingGarment) => void) | undefined;
  onViewPhotos?: ((garment: BookingGarment) => void) | undefined;
  /** Opens the garment timeline. Absent on a surface that has nowhere to put a panel. */
  onShowHistory?: ((garment: BookingGarment) => void) | undefined;
  onPrintDocket?: ((garment: BookingGarment) => void) | undefined;
}) {
  const [open, setOpen] = useState(false);

  const hasDetail =
    garment.alterations.length > 0 || garment.accessories.length > 0 || Boolean(garment.notes);

  // Only offered where the server would accept something. A garment that is `Ready` moves next at
  // the issue counter, not from here, so the row shows no control rather than one that 400s.
  const movable = onMoveGarment !== undefined && movesFor(garment.state).length > 0;

  return (
    <li>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 ps-8">
        {/* The picture the counter checks against, at the catalogue's shape, so the booking reads as
            the bag it was taken from. Opens the viewer when there is one; with none, the outline
            holds the same space so the rows do not jag. A booking does not carry the category, so
            the design's own name picks the outline — "Black Jodhpuri Bandhgala" draws a bandhgala. */}
        {onViewPhotos && garment.imageUrl ? (
          <button
            type="button"
            onClick={() => onViewPhotos(garment)}
            aria-label={`View ${garment.serialCode}`}
            className="w-10 shrink-0 cursor-pointer overflow-hidden rounded hover:opacity-85"
          >
            <DesignPhoto
              url={garment.imageUrl}
              alt={garment.serialCode}
              categoryPath={garment.itemName}
            />
          </button>
        ) : (
          <div className="w-10 shrink-0 overflow-hidden rounded">
            <DesignPhoto
              url={garment.imageUrl}
              alt={garment.serialCode}
              categoryPath={garment.itemName}
            />
          </div>
        )}

        <span className="min-w-0 flex-1">
          <span className="text-[13px] font-medium text-gray-900">{garment.itemName}</span>
          <span className="text-default block font-mono text-[11px]">
            {withReference(garment.serialCode, garment.referenceNumber)}
          </span>
        </span>

        <span className="flex flex-col items-start gap-0.5">
          <StatusBadge tone={garmentTone(garment.state)}>{humanizeEnum(garment.state)}</StatusBadge>
          {garment.state === 'Alteration' && garment.alterationTailorName && (
            <span className="text-default text-[11px]">
              {garment.alterationTailorName}
              {garment.alterationDueOn && ` · due ${formatDate(garment.alterationDueOn)}`}
            </span>
          )}
        </span>

        {/* Struck rate over the charged one where this garment carries its own concession, which is
            how a customer reads a discount — a single reduced number tells them nothing was given. */}
        <span className="w-20 text-end">
          <span
            className={`font-mono-nums block text-[13px] ${
              garment.discountAmount ? 'text-default line-through' : 'text-gray-900'
            }`}
          >
            {currency}
            {formatAmount(garment.rentalRate)}
          </span>

          {garment.discountAmount ? (
            <span className="font-mono-nums text-primary block text-[13px]">
              {currency}
              {formatAmount(garment.netRentalRate)}
            </span>
          ) : null}
        </span>

        {/* One garment's docket, off the row the clerk is already looking at. Offered on the same
            condition the print module itself uses — measurements, not state and not a job number —
            because a sheet with a heading and no work on it is worse than no sheet. */}
        {onPrintDocket && garment.alterations.length > 0 && (
          <button
            type="button"
            onClick={() => onPrintDocket(garment)}
            className="border-border-color hover:bg-light flex h-7 cursor-pointer items-center gap-1.5 rounded border bg-white px-2 text-[11.5px] text-gray-900"
          >
            <i className="icon icon-scissors text-[12px]" />
            Docket
            <span className="sr-only">for {garment.serialCode}</span>
          </button>
        )}

        {/* Where this garment has been, from the row that is holding it up. §5 names this as the
            third place the timeline is reached from — "is this the one that came back marked?" is
            asked at the counter, about a garment already on a booking. */}
        {onShowHistory && (
          <button
            type="button"
            onClick={() => onShowHistory(garment)}
            className="border-border-color hover:bg-light flex h-7 cursor-pointer items-center gap-1.5 rounded border bg-white px-2 text-[11.5px] text-gray-900"
          >
            <i className="icon icon-history text-[12px]" />
            History
            <span className="sr-only">of {garment.serialCode}</span>
          </button>
        )}
        {movable && (
          <button
            type="button"
            onClick={() => onMoveGarment?.(garment)}
            className="border-border-color hover:bg-light flex h-7 cursor-pointer items-center gap-1.5 rounded border bg-white px-2 text-[11.5px] text-gray-900"
          >
            <i className="icon icon-arrow-right text-[12px]" />
            Move
            <span className="sr-only">{garment.serialCode} to another state</span>
          </button>
        )}

        {hasDetail ? (
          <button
            type="button"
            onClick={() => setOpen((was) => !was)}
            aria-expanded={open}
            className="border-border-color hover:bg-light flex size-7 cursor-pointer items-center justify-center rounded border bg-white"
          >
            <i
              className={`icon text-[12px] text-gray-900 ${open ? 'icon-chevron-up' : 'icon-chevron-down'}`}
            />
            <span className="sr-only">
              {open ? 'Hide' : 'Show'} details for {garment.serialCode}
            </span>
          </button>
        ) : (
          <span className="size-7" />
        )}
      </div>

      {open && <GarmentDetail garment={garment} currency={currency} />}
    </li>
  );
}

/**
 * Everything about one garment that does not fit on its row.
 *
 * <p>
 * Three panels, and they are three different <em>kinds</em> of fact — which is why they are not one
 * notes field. The alteration is structured, because the same rows print the tailor's docket and
 * drive the reversal on return. The accessories are a checklist with a charge each, because
 * "missing accessories, ₹300" deducted from a deposit is indefensible unless something recorded
 * what left the shop. The counter notes stay free text, because what a shop needs to write down is
 * never the thing you gave it a field for.
 * </p>
 */
function GarmentDetail({ garment, currency }: { garment: BookingGarment; currency: string }) {
  return (
    <div className="bg-light grid gap-3 px-4 pb-3 ps-8 md:grid-cols-3">
      {garment.alterations.length > 0 && (
        <Panel title={garment.alterationJobNumber ?? 'Alteration'}>
          {/* The same narrative the tailor's docket prints, so the counter and the workshop are
              never reading two different descriptions of one job. */}
          <p className="text-[12px] leading-relaxed text-gray-900">
            {alterationNarrative(garment.alterations)}
          </p>
          <dl className="mt-2 space-y-0.5 text-[11.5px]">
            {garment.alterationCharge !== null && (
              <Row label="Charge">
                {currency}
                {formatMoney(garment.alterationCharge)}
              </Row>
            )}
            <Row label="Reversible">
              {garment.alterationIsReversible ? 'Yes — let back out on return' : 'No — a repair'}
            </Row>
          </dl>
        </Panel>
      )}

      {garment.accessories.length > 0 && (
        <Panel title="Goes out with it">
          <ul className="space-y-1 text-[12px]">
            {garment.accessories.map((accessory) => (
              <li key={accessory.id} className="flex items-center gap-2">
                <i
                  className={`icon text-[12px] ${
                    accessory.issued ? 'icon-square-check text-primary' : 'icon-square text-default'
                  }`}
                />
                <span className={accessory.issued ? 'text-gray-900' : 'text-default'}>
                  {accessory.name}
                </span>
                <span className="text-default font-mono-nums ms-auto text-[11px]">
                  ×{accessory.quantity}
                  {accessory.replacementCharge !== null &&
                    ` · ${currency}${formatAmount(accessory.replacementCharge)}`}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {garment.notes && (
        <Panel title="Counter notes">
          <p className="text-default text-[12px] leading-relaxed whitespace-pre-line">
            {garment.notes}
          </p>
        </Panel>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-border-color rounded-md border bg-white p-3">
      <p className="text-default mb-2 text-[10px] font-semibold tracking-wider uppercase">
        {title}
      </p>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-default">{label}</dt>
      <dd className="m-0 text-gray-900">{children}</dd>
    </div>
  );
}

/** Two letters for the avatar. A single-word name gives one rather than borrowing from nowhere. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
